import type { CoviChatItem, CoviThreadMeta } from "../model/types"
import { deleteOrgThreadState, loadOrgState, saveOrgState } from "./storage"
import { DEFAULT_THREAD_TITLE, deriveThreadTitle } from "./threadTitle"

/**
 * The per-org thread registry: every thread the user has, ordered metadata plus
 * which one is showing. Only metadata lives here (titles/timestamps/counts) —
 * each thread's transcript lives under its own `storage.ts` slot, so the thread
 * list renders without deserializing a single transcript.
 *
 * This is the ONLY record of a thread. There is no separate "open tabs" vs
 * "archived history" split any more: switching to an old thread is the same
 * operation as switching to a recent one. Threads leave only through an
 * explicit delete or storage-cap pruning. Version 2 folds the old
 * `coval:copilot:conversations:{org}` archive into this registry (see
 * `adoptArchivedConversations`). Version 3 adds durable thread pinning;
 * version 4 adds durable unread-update state.
 */

const THREADS_VERSION = 4
const PINNED_THREADS_VERSION = 3
const PREVIOUS_THREADS_VERSION = 2
const LEGACY_THREADS_VERSION = 1

/**
 * Cap on stored threads. Threads cost no screen space now, so this is purely a
 * localStorage-quota guard — a single Sofia reply can run 25-30k characters, so
 * an uncapped registry would eventually wedge the whole panel's persistence.
 */
export const MAX_THREADS = 50

interface StoredThreads {
  version: number
  activeThreadId: string | null
  threads: CoviThreadMeta[]
}

/** Not exported — callers use the inferred shape of `loadThreads`. */
interface CoviThreadRegistry {
  activeThreadId: string | null
  threads: CoviThreadMeta[]
}

interface LoadedCoviThreadRegistry extends CoviThreadRegistry {
  /**
   * False when at least one legacy transcript could not be copied. Callers must
   * not persist the partial registry in the current schema or the next load
   * cannot retry.
   */
  migrationComplete: boolean
}

/** A fresh empty registry — never a shared instance callers could mutate. */
function emptyRegistry(migrationComplete = true): LoadedCoviThreadRegistry {
  return { activeThreadId: null, threads: [], migrationComplete }
}

function threadsKey(orgSlug: string): string {
  return `coval:copilot:threads:${orgSlug}`
}

/** Pre-v2 archive of closed conversations, folded into the registry on load. */
function legacyConversationsKey(orgSlug: string): string {
  return `coval:copilot:conversations:${orgSlug}`
}

function legacyConversationSummariesKey(orgSlug: string): string {
  return `coval:copilot:conversation-summaries:${orgSlug}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object"
}

function isValidThreadMeta(value: unknown): value is CoviThreadMeta {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    value.id.length > 0 &&
    typeof value.title === "string" &&
    typeof value.titlePinned === "boolean" &&
    typeof value.pinned === "boolean" &&
    typeof value.unread === "boolean" &&
    typeof value.createdAt === "number" &&
    Number.isFinite(value.createdAt) &&
    typeof value.updatedAt === "number" &&
    Number.isFinite(value.updatedAt) &&
    typeof value.messageCount === "number" &&
    Number.isFinite(value.messageCount)
  )
}

function normalizeThreadMeta(value: unknown): CoviThreadMeta | null {
  if (!isRecord(value)) {
    return null
  }
  // Older preview builds persisted a presentation mode. Chats now have one
  // presentation, so discard the stale field while normalizing the registry.
  const rest = { ...value }
  delete rest.mode
  const normalized = {
    ...rest,
    pinned: typeof value.pinned === "boolean" ? value.pinned : false,
    unread: typeof value.unread === "boolean" ? value.unread : false,
  }
  return isValidThreadMeta(normalized) ? normalized : null
}

/**
 * Trim to MAX_THREADS. Empty threads go first (nothing to lose), then the
 * least-recently-updated — and pinned or resident threads are never candidates. Returns
 * the surviving list plus the ids whose transcript slots the caller must drop.
 * If every excess thread is protected, the registry may temporarily exceed the
 * cap rather than destroy an in-flight turn or unsent draft.
 */
export function pruneThreads(
  threads: CoviThreadMeta[],
  protectedThreadIds: ReadonlySet<string>,
): { threads: CoviThreadMeta[]; evicted: string[] } {
  if (threads.length <= MAX_THREADS) {
    return { threads, evicted: [] }
  }
  const expendable = threads
    .filter((thread) => !thread.pinned && !protectedThreadIds.has(thread.id))
    .sort((a, b) => {
      const emptiness = Number(a.messageCount > 0) - Number(b.messageCount > 0)
      return emptiness !== 0 ? emptiness : a.updatedAt - b.updatedAt
    })
  const evicted = new Set(
    expendable.slice(0, threads.length - MAX_THREADS).map((t) => t.id),
  )
  return {
    threads: threads.filter((thread) => !evicted.has(thread.id)),
    evicted: [...evicted],
  }
}

/**
 * Fold the pre-thread single-transcript key (`coval:copilot:{orgSlug}`) into a
 * first thread ("0") so an existing user's in-progress chat becomes a thread
 * instead of vanishing. The source key is retired only after every migration
 * step and the current registry commit; until then it remains the retryable
 * copy.
 */
function adoptLegacyTranscript(orgSlug: string): {
  threads: CoviThreadMeta[]
  complete: boolean
} {
  const legacy = loadOrgState(orgSlug)
  if (legacy.items.length === 0) {
    return { threads: [], complete: true }
  }
  if (!saveOrgState(orgSlug, { items: legacy.items }, "0")) {
    return { threads: [], complete: false }
  }
  const now = Date.now()
  return {
    threads: [
      {
        id: "0",
        title: deriveThreadTitle(legacy.items),
        titlePinned: false,
        pinned: false,
        unread: false,
        createdAt: now,
        updatedAt: now,
        messageCount: legacy.items.length,
      },
    ],
    complete: true,
  }
}

interface LegacyConversation {
  id: string
  title: string
  items: CoviChatItem[]
  createdAt: number
  updatedAt: number
}

function readLegacyConversations(orgSlug: string): LegacyConversation[] {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(legacyConversationsKey(orgSlug))
  } catch {
    return []
  }
  if (!raw) {
    return []
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || !Array.isArray(parsed.conversations)) {
      return []
    }
    // Structural check only — each transcript is deep-sanitized by loadOrgState
    // when its thread is opened, exactly as an ordinary thread's would be.
    return parsed.conversations.filter(
      (conversation): conversation is LegacyConversation =>
        isRecord(conversation) &&
        typeof conversation.id === "string" &&
        conversation.id.length > 0 &&
        typeof conversation.title === "string" &&
        Array.isArray(conversation.items) &&
        conversation.items.length > 0 &&
        typeof conversation.createdAt === "number" &&
        Number.isFinite(conversation.createdAt) &&
        typeof conversation.updatedAt === "number" &&
        Number.isFinite(conversation.updatedAt),
    )
  } catch {
    return []
  }
}

function dropLegacyConversations(orgSlug: string): void {
  try {
    window.localStorage.removeItem(legacyConversationsKey(orgSlug))
    window.localStorage.removeItem(legacyConversationSummariesKey(orgSlug))
  } catch {
    // Non-fatal: the keys linger but nothing reads them once migration commits.
  }
}

/**
 * Turn every archived conversation into an ordinary thread, so "previous chats"
 * and "open tabs" become one list.
 *
 * The archive is only dropped once BOTH each transcript slot and the current
 * registry that makes those threads discoverable are durably written. If
 * either write is swallowed (quota / private mode) the archive stays put and
 * migration simply retries on the next load — losing a user's history to a
 * failed write is the one outcome worth being paranoid about here.
 *
 * A conversation keeps its own id as its thread id: already unique, and
 * disjoint from the `t_`-prefixed ids the provider mints.
 */
function adoptArchivedConversations(
  orgSlug: string,
  existing: CoviThreadMeta[],
): { threads: CoviThreadMeta[]; complete: boolean } {
  const conversations = readLegacyConversations(orgSlug)
  if (conversations.length === 0) {
    return { threads: existing, complete: true }
  }
  const known = new Set(existing.map((thread) => thread.id))
  const adopted: CoviThreadMeta[] = []
  let complete = true
  for (const conversation of conversations) {
    if (known.has(conversation.id)) {
      continue
    }
    if (
      !saveOrgState(orgSlug, { items: conversation.items }, conversation.id)
    ) {
      complete = false
      continue
    }
    adopted.push({
      id: conversation.id,
      title: conversation.title.trim() || deriveThreadTitle(conversation.items),
      // Archived titles were auto-derived by the old truncating deriver; leave
      // them unpinned so a user rename is still the only thing that pins.
      titlePinned: false,
      pinned: false,
      unread: false,
      createdAt: conversation.createdAt,
      updatedAt: conversation.updatedAt,
      messageCount: conversation.items.length,
    })
  }
  return { threads: [...existing, ...adopted], complete }
}

function parseRegistry(raw: string | null): {
  registry: CoviThreadRegistry
  version: number
} | null {
  if (!raw) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || typeof parsed.version !== "number") {
      return null
    }
    if (
      parsed.version !== THREADS_VERSION &&
      parsed.version !== PINNED_THREADS_VERSION &&
      parsed.version !== PREVIOUS_THREADS_VERSION &&
      parsed.version !== LEGACY_THREADS_VERSION
    ) {
      return null
    }
    const candidate = parsed as Partial<StoredThreads>
    const threads = Array.isArray(candidate.threads)
      ? candidate.threads
          .map(normalizeThreadMeta)
          .filter((thread): thread is CoviThreadMeta => thread !== null)
      : []
    // Only trust an active id that still points at a live thread.
    const activeThreadId =
      typeof candidate.activeThreadId === "string" &&
      threads.some((thread) => thread.id === candidate.activeThreadId)
        ? candidate.activeThreadId
        : (threads[0]?.id ?? null)
    return { registry: { activeThreadId, threads }, version: parsed.version }
  } catch {
    return null
  }
}

/**
 * Load the registry, running whatever migration this org still needs. A v1
 * registry (open tabs only) has the archived conversations folded in and is
 * rewritten as v4; v2/v3 registries are normalized with pin and unread
 * defaults; a browser with no registry at all adopts the pre-thread single
 * transcript.
 */
export function loadThreads(
  orgSlug: string | null | undefined,
): LoadedCoviThreadRegistry {
  if (!orgSlug || typeof window === "undefined") {
    return emptyRegistry()
  }
  try {
    const stored = parseRegistry(
      window.localStorage.getItem(threadsKey(orgSlug)),
    )
    if (stored?.version === THREADS_VERSION) {
      return { ...stored.registry, migrationComplete: true }
    }

    const legacy = stored
      ? { threads: stored.registry.threads, complete: true }
      : adoptLegacyTranscript(orgSlug)
    const archived = adoptArchivedConversations(orgSlug, legacy.threads)
    const complete = legacy.complete && archived.complete
    const threads = archived.threads
    if (threads.length === 0) {
      return emptyRegistry(complete)
    }
    const activeThreadId =
      stored?.registry.activeThreadId ??
      [...threads].sort((a, b) => b.updatedAt - a.updatedAt)[0].id
    const migrated: CoviThreadRegistry = { activeThreadId, threads }
    // Retire both legacy sources only once every transcript and the current
    // registry are on disk. Until then either source may be the only retryable
    // copy.
    if (complete && saveThreads(orgSlug, migrated)) {
      deleteOrgThreadState(orgSlug)
      dropLegacyConversations(orgSlug)
    }
    return { ...migrated, migrationComplete: complete }
  } catch {
    return emptyRegistry()
  }
}

/**
 * Persist the registry. Returns `true` when the write reached localStorage and
 * `false` on a swallowed failure (quota exceeded / private mode) — migration
 * uses the result to decide whether it may drop the copy it migrated from.
 */
export function saveThreads(
  orgSlug: string | null | undefined,
  registry: CoviThreadRegistry,
): boolean {
  if (!orgSlug || typeof window === "undefined") {
    return false
  }
  try {
    window.localStorage.setItem(
      threadsKey(orgSlug),
      JSON.stringify({
        version: THREADS_VERSION,
        activeThreadId: registry.activeThreadId,
        threads: registry.threads,
      } satisfies StoredThreads),
    )
    return true
  } catch {
    // Quota exceeded / private mode — drop silently; threads stay in-memory.
    return false
  }
}

/** A blank thread, ready for the registry. */
export function newThreadMeta(id: string, now: number): CoviThreadMeta {
  return {
    id,
    title: DEFAULT_THREAD_TITLE,
    titlePinned: false,
    pinned: false,
    unread: false,
    createdAt: now,
    updatedAt: now,
    messageCount: 0,
  }
}
